import { SystemStyleObject } from '@chakra-ui/react';
import { APPOINTMENT_ROUTE } from '@web-app/components/consts';
import { getPatientAppUrl } from '@web-app/utils/env.utils';

import { ROOM_ERROR_TYPES, RoomErrorType } from './types';

export interface ErrorScreenConfig {
  title: string;
  description: string;
  secondaryText?: string;
  showSpinner?: boolean;
  showButton?: boolean;
  buttonText?: string;
  buttonColorScheme?: string;
  buttonHoverBg?: string;
  buttonHref?: string; // For external link redirects
  logoClickable?: boolean; // Whether logo should be a clickable link (default: true)
}

export const ERROR_CONFIGS: Record<RoomErrorType, ErrorScreenConfig> = {
  [ROOM_ERROR_TYPES.RECREATING_ROOM]: {
    title: 'Verifying Meeting Room',
    description: 'Recreating meeting room, please wait...',
    showSpinner: true,
  },
  [ROOM_ERROR_TYPES.RECREATE_SUCCESS]: {
    title: 'Meeting Room Ready',
    description: 'Connecting to a new room...',
    showSpinner: true,
  },
  [ROOM_ERROR_TYPES.APPOINTMENT_CANCELLED]: {
    title: 'Appointment Unavailable',
    description:
      'This appointment is inactive and the meeting room is no longer available',
    secondaryText:
      'Please contact support if this is an error or book a new appointment',
  },
  [ROOM_ERROR_TYPES.APPOINTMENT_NOT_FOUND]: {
    title: 'Appointment Not Found',
    description:
      'This appointment is inactive and the meeting room is no longer available',
    secondaryText:
      'Please contact support if this is an error or book a new appointment',
  },
  [ROOM_ERROR_TYPES.APPOINTMENT_ENDED_PROVIDER]: {
    title: 'Link Inactive',
    description:
      'Please head to Appointments page to navigate to the accurate appointment or generate a new link',
    secondaryText: '',
    showButton: true,
    buttonText: 'See My Appointments',
    buttonHref: APPOINTMENT_ROUTE,
    buttonColorScheme: 'orange',
    buttonHoverBg: 'orange.600',
  },
  [ROOM_ERROR_TYPES.APPOINTMENT_ENDED_PATIENT]: {
    title: 'This link has expired',
    description:
      'Please head to back to the app to join the updated appointment link!',
    secondaryText: '',
    showButton: true,
    buttonText: 'See My Appointments',
    buttonHref: `${getPatientAppUrl()}`,
    buttonColorScheme: 'orange',
    buttonHoverBg: 'orange.600',
    logoClickable: false,
  },
  [ROOM_ERROR_TYPES.RECREATE_FAILED]: {
    title: 'Unable to Join Meeting',
    description: 'We encountered an error while preparing your meeting room.',
    showButton: true,
    buttonText: 'Try Again',
    buttonColorScheme: 'green',
    buttonHoverBg: 'green.400',
  },
  [ROOM_ERROR_TYPES.UNKNOWN_ERROR]: {
    title: 'Connection Error',
    description: 'An unexpected error occurred while joining the meeting.',
    showButton: true,
    buttonText: 'Try Again',
    buttonColorScheme: 'green',
    buttonHoverBg: 'green.400',
  },
};

export const styles: Record<string, SystemStyleObject> = {
  container: {
    position: 'relative',
    mx: 'auto',
    h: '100vh',
    flexDirection: 'column',
    textAlign: 'center',
    backgroundColor: 'green.100',
  },
  logoContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    py: 6,
    display: 'flex',
    justifyContent: 'center',
  },
  contentWrapper: {
    flex: 1,
    justifyContent: 'center',
    px: '2rem',
  },
  card: {
    maxW: '600px',
    bg: 'white',
    borderRadius: '2xl',
    p: 14,
    boxShadow: 'sm',
  },
  heading: {
    color: 'green.800',
  },
  primaryText: {
    maxW: '450px',
    fontSize: 'md',
    color: 'gray.600',
    my: 2,
  },
  secondaryText: {
    fontSize: 'sm',
    color: 'gray.500',
  },
};
